import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { AudioWaveform, type WaveformVariant } from "./AudioWaveform";
import { personaAvatarUrl } from "../lib/avatar";

interface CallStageProps {
  /** Nom de l'interlocuteur affiché (ex. scenario.prospectName). */
  prospectName: string;
  /** Ligne de contexte sous le titre (ex. "Prospection téléphonique · CTO"). */
  subtitle?: string;
  /** Portrait pro (URL signée R2) attribué au scénario. Repli randomuser sinon. */
  avatarUrl?: string | null;
  /** True quand l'agent parle (halo + waveform actifs). */
  isSpeaking: boolean;
  /** Transcription live de la réplique en cours (sous-titre cinéma). */
  liveTranscript: string;
  /** Flux audio de l'agent pour le visualiseur. */
  agentStream: MediaStream | null;
  /** Couleur d'accent du simulateur. */
  color: string;
  /** Style de waveform. Défaut : barres miroir (ouvertes au repos). */
  waveformVariant?: WaveformVariant;
  t: TFunction;
}

// Couleur de la waveform : bleu ciel charte. Au repos elle reste atténuée
// (grisée), et vire vers ce bleu vif quand la voix de l'agent s'active.
const WAVEFORM_COLOR = "#3E92CC";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]!;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1]!;
  return (first[0]! + last[0]!).toUpperCase();
}

/**
 * Scène d'appel "visioconférence" affichée pendant la simulation vocale.
 * Avatar de l'interlocuteur (placeholder initiales pour l'instant — la banque
 * d'avatars arrive au Lot 4), sous-titre live de la réplique en cours, et
 * waveform synchronisée à la prise de parole de l'IA.
 *
 * Le bloc de feedback temps réel (score, points forts) est volontairement hors
 * scope ici — traité dans un second temps.
 */
export function CallStage({
  prospectName,
  subtitle,
  avatarUrl,
  isSpeaking,
  liveTranscript,
  agentStream,
  color,
  waveformVariant = "bars",
  t,
}: CallStageProps) {
  // Détection "l'agent parle" robuste : on combine l'état isAgentSpeaking du hook
  // ET les mises à jour du sous-titre live (mêmes events delta) — si l'un des deux
  // signaux indique de l'activité, la waveform s'anime. La fenêtre de 700 ms après
  // le dernier delta évite les micro-coupures entre chunks de transcription.
  const [transcriptActive, setTranscriptActive] = useState(false);
  useEffect(() => {
    if (!liveTranscript) return;
    setTranscriptActive(true);
    const id = setTimeout(() => setTranscriptActive(false), 700);
    return () => clearTimeout(id);
  }, [liveTranscript]);
  const speaking = isSpeaking || transcriptActive;

  // Portrait pro attribué au scénario (R2) en priorité ; sinon repli sur un
  // visage de banque stable par nom ; sinon initiales (si l'image échoue).
  const resolvedAvatar = useMemo(
    () => (avatarUrl && avatarUrl.length > 0 ? avatarUrl : personaAvatarUrl(prospectName)),
    [avatarUrl, prospectName],
  );
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [resolvedAvatar]);

  // Sous-titre live : sur mobile sa hauteur est plafonnée (sinon un long texte
  // pousse la waveform hors de l'écran). On garde la fin visible (auto-scroll).
  const subtitleRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = subtitleRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveTranscript]);

  return (
    <div className="flex flex-col items-center gap-5 py-4 sm:py-6">
      {/* Bandeau "Simulation en cours" */}
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        {t("training.simulationInProgress")}
      </div>

      {/* Avatar + sous-titre */}
      <div className="flex w-full flex-col items-center gap-5 sm:flex-row sm:items-center sm:justify-center sm:gap-8">
        {/* Avatar avec halo pulsant quand l'agent parle */}
        <div className="relative shrink-0">
          <div
            className={`absolute inset-0 rounded-full transition-opacity duration-300 ${
              speaking ? "opacity-100 animate-pulse" : "opacity-0"
            }`}
            style={{ boxShadow: `0 0 0 8px ${color}33, 0 0 32px 4px ${color}55` }}
          />
          {imgError ? (
            <div
              className="relative flex h-28 w-28 items-center justify-center rounded-full text-3xl font-semibold text-white shadow-lg sm:h-36 sm:w-36"
              style={{
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                outline: `3px solid ${color}`,
                outlineOffset: "3px",
              }}
            >
              {initials(prospectName)}
            </div>
          ) : (
            <img
              src={resolvedAvatar}
              alt={prospectName}
              onError={() => setImgError(true)}
              className="relative h-28 w-28 rounded-full object-cover shadow-lg sm:h-36 sm:w-36"
              style={{ outline: `3px solid ${color}`, outlineOffset: "3px" }}
            />
          )}
        </div>

        {/* Bulle sous-titre live */}
        <div className="flex min-h-[5rem] w-full max-w-md flex-1 items-center justify-center sm:justify-start">
          <div
            ref={subtitleRef}
            className="max-h-28 w-full overflow-y-auto rounded-2xl bg-muted px-5 py-3.5 text-center sm:max-h-none sm:text-left"
          >
            <p className="text-base leading-relaxed text-foreground sm:text-lg">
              {liveTranscript ? (
                liveTranscript
              ) : (
                <span className="italic text-muted-foreground">
                  {t("training.listeningSubtitle")}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Nom + contexte */}
      <div className="text-center">
        <p className="text-lg font-semibold" style={{ color }}>
          {prospectName}
        </p>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>

      {/* Waveform */}
      <div className="h-24 w-full max-w-md sm:h-28">
        <AudioWaveform stream={agentStream} active={speaking} color={WAVEFORM_COLOR} variant={waveformVariant} />
      </div>
    </div>
  );
}
